import { useState, useEffect } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import Sidebar from './components/Sidebar';
import { Toast } from './components/UI';
import Dashboard from './pages/Dashboard';
import Families from './pages/Families';
import Expenses from './pages/Expenses';
import Settlement from './pages/Settlement';
import TripInfo from './pages/TripInfo';
import { api, getToken, saveToken } from './utils/api';

// ── Login Screen ─────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [pw,      setPw]      = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.login(pw);
      if (res.token) {
        saveToken(res.token); // stored in localStorage for 1 year
        onLogin();
      } else {
        setError(res.error || 'Invalid password');
        setPw('');
      }
    } catch {
      setError('Could not reach server. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: 'var(--bg-primary)',
    }}>
      <div className="glass-card" style={{ width: '100%', maxWidth: '380px', textAlign: 'center', padding: '2.5rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✈️</div>
        <h2 style={{ fontFamily: 'var(--font-heading)', marginBottom: '0.4rem' }}>Tour Expenses</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '2rem' }}>Enter password to continue</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ textAlign: 'left' }}>
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={pw}
              onChange={e => setPw(e.target.value)}
              placeholder="••••••••"
              autoFocus
              style={{ borderColor: error ? 'var(--accent-red)' : undefined }}
            />
            {error && (
              <p style={{ color: 'var(--accent-red-light)', fontSize: '0.8rem', marginTop: '0.4rem' }}>{error}</p>
            )}
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !pw}
            style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem' }}
          >
            {loading ? 'Verifying...' : 'Enter'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Main App Shell ───────────────────────────────────────────
function AppShell() {
  const { loadState, loading, error } = useApp();
  const [view,        setView]    = useState('dashboard');
  const [sidebarOpen, setSidebar] = useState(false);

  useEffect(() => { loadState(); }, [loadState]);

  const renderView = () => {
    switch (view) {
      case 'dashboard':  return <Dashboard />;
      case 'families':   return <Families />;
      case 'expenses':   return <Expenses />;
      case 'settlement': return <Settlement />;
      case 'info':       return <TripInfo />;
      default:           return <Dashboard />;
    }
  };

  if (loading) return (
    <div className="app-loading">
      <div className="loading-spinner" />
      <p>Connecting to database...</p>
    </div>
  );

  if (error) return (
    <div className="app-loading">
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
      <h2>Connection Failed</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>{error}</p>
      <button className="btn btn-primary" onClick={loadState}>Retry</button>
    </div>
  );

  const NAV_ICONS = [
    { view: 'dashboard',  label: 'Dash',    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
    { view: 'families',   label: 'Family',  icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
    { view: 'expenses',   label: 'Expense', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
    { view: 'settlement', label: 'Settle',  icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg> },
    { view: 'info',       label: 'Info',    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg> },
  ];

  return (
    <div className="app-layout">
      {/* Mobile top header */}
      <div className="mobile-header">
        <button className="menu-toggle" onClick={() => setSidebar(o => !o)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1rem' }}>✈️ Tour Expenses</span>
        <div style={{ width: 24 }} />
      </div>

      {/* Sidebar — desktop always visible, mobile slide-in */}
      <Sidebar
        currentView={view}
        onNavigate={(v) => { setView(v); setSidebar(false); }}
        isOpen={sidebarOpen}
        onClose={() => setSidebar(false)}
      />

      <main className="main" id="main-content">{renderView()}</main>

      {/* Mobile bottom nav */}
      <nav className="mobile-bottom-nav">
        {NAV_ICONS.map(item => (
          <button
            key={item.view}
            className={`nav-item${view === item.view ? ' active' : ''}`}
            onClick={() => setView(item.view)}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      <Toast />
    </div>
  );
}

// ── Root ─────────────────────────────────────────────────────
export default function App() {
  // Check localStorage token with expiry validation
  const [authed, setAuthed] = useState(() => !!getToken());

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  return (
    <AppProvider>
      <AppShell />
    </AppProvider>
  );
}
